/**
 * Phase E · Part A — Contract Risk Scanner.
 * Pick a contract, optionally paste its text or upload the PDF, and let Claude
 * flag the high-risk clauses (LD, liability, payment, termination, indemnity…).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  ShieldAlert, ScanLine, Loader2, FileText, Upload, History, AlertTriangle, X,
} from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';

const LEVEL_STYLE = {
  Critical: 'bg-rose-100 text-rose-800 border-rose-300',
  High: 'bg-orange-100 text-orange-800 border-orange-300',
  Medium: 'bg-amber-100 text-amber-800 border-amber-300',
  Low: 'bg-slate-100 text-slate-600 border-slate-300',
};
const ROW_TINT = {
  Critical: 'bg-rose-50',
  High: 'bg-orange-50/60',
  Medium: 'bg-amber-50/50',
  Low: '',
};

import { formatDateTime as fmt } from '../lib/format';

export default function ContractRiskScanner({ projectId }) {
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState('');
  const [scan, setScan] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [pdfB64, setPdfB64] = useState(null);
  const [pdfName, setPdfName] = useState('');

  useEffect(() => {
    const qs = projectId ? `?project_id=${projectId}` : '';
    apiGet(`/contracts${qs}`).then((r) => {
      const items = r.items || [];
      setContracts(items);
      setContractId((cur) => (items.find((c) => c.id === cur) ? cur : (items[0]?.id || '')));
    }).catch(() => toast.error('Could not load contracts'));
  }, [projectId]);

  const loadScan = useCallback(async (cid) => {
    if (!cid) { setScan(null); setHistory([]); return; }
    setLoading(true);
    try {
      const [latest, hist] = await Promise.all([
        apiGet(`/ai/contracts/${cid}/risk-scan`),
        apiGet(`/ai/contracts/${cid}/risk-scans`),
      ]);
      setScan(latest.scan || null);
      setHistory(hist.history || []);
    } catch { /* may be unscanned yet */ setScan(null); setHistory([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadScan(contractId); }, [contractId, loadScan]);

  const onPdf = (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('PDF must be under 25 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => { setPdfB64(reader.result); setPdfName(file.name); setPastedText(''); };
    reader.readAsDataURL(file);
  };

  const runScan = async () => {
    if (!contractId) { toast.error('Pick a contract first'); return; }
    setScanning(true);
    try {
      const body = {};
      if (pastedText.trim()) body.pasted_text = pastedText.trim();
      else if (pdfB64) { body.pdf_base64 = pdfB64; body.filename = pdfName; }
      const r = await apiPost(`/ai/contracts/${contractId}/risk-scan`, body);
      setScan(r.scan);
      toast.success(`Scan complete — overall ${r.scan.overall_risk_level}`);
      setShowInput(false); setPastedText(''); setPdfB64(null); setPdfName('');
      loadScan(contractId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Risk scan failed');
    } finally { setScanning(false); }
  };

  const selected = contracts.find((c) => c.id === contractId);

  return (
    <div className="space-y-4" data-testid="contract-risk-scanner">
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Contract</label>
          <select
            value={contractId}
            onChange={(e) => setContractId(e.target.value)}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
            data-testid="cr-contract-picker"
          >
            <option value="">— Select a contract —</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>{c.contract_no || c.title || c.id} · {c.client || '—'}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowInput((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-md border border-slate-300 text-slate-600 hover:border-[#3b4872]"
          data-testid="cr-toggle-input"
        >
          <FileText size={13} /> Paste / Upload
        </button>
        <button
          onClick={runScan}
          disabled={scanning || !contractId}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-md bg-[#3b4872] text-white hover:bg-[#2c365a] disabled:opacity-50"
          data-testid="cr-scan-btn"
        >
          {scanning ? <Loader2 size={13} className="animate-spin" /> : <ScanLine size={13} />}
          {scanning ? 'Scanning…' : scan ? 'Re-scan' : 'Scan for Risks'}
        </button>
      </div>

      {showInput && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="cr-input-panel">
          <div className="text-xs text-slate-500">
            Most contracts have no machine-readable text stored. Paste the contract text or upload the PDF for a thorough scan. Leave blank to scan the stored clause fields.
          </div>
          <textarea
            value={pastedText}
            onChange={(e) => { setPastedText(e.target.value); if (e.target.value) { setPdfB64(null); setPdfName(''); } }}
            rows={5}
            placeholder="Paste contract / GCC / SCC clauses here…"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            data-testid="cr-paste-text"
          />
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-md border border-slate-300 text-slate-600 hover:border-[#3b4872] cursor-pointer" data-testid="cr-pdf-label">
              <Upload size={13} /> Upload PDF
              <input type="file" accept=".pdf" className="hidden" onChange={(e) => onPdf(e.target.files?.[0])} data-testid="cr-pdf-input" />
            </label>
            {pdfName && <span className="text-xs text-slate-600 inline-flex items-center gap-1"><FileText size={12} /> {pdfName}<button onClick={() => { setPdfB64(null); setPdfName(''); }}><X size={12} /></button></span>}
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
      ) : !contractId ? (
        <div className="p-12 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-lg">Select a contract to view or run a risk scan.</div>
      ) : !scan ? (
        <div className="p-12 text-center text-sm text-slate-500 bg-white border border-dashed border-slate-300 rounded-lg" data-testid="cr-empty">
          <ShieldAlert size={28} className="mx-auto mb-2 text-slate-300" />
          No risk scan yet for <b>{selected?.contract_no || 'this contract'}</b>. Click <b>Scan for Risks</b> to run one.
        </div>
      ) : (
        <div className="space-y-3" data-testid="cr-results">
          {/* Overall badge */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-display font-bold uppercase border ${LEVEL_STYLE[scan.overall_risk_level] || LEVEL_STYLE.Low}`} data-testid="cr-overall-badge">
              Overall: {scan.overall_risk_level}
            </span>
            <span className="text-sm text-slate-600" data-testid="cr-counts">
              {scan.critical_count} critical · {scan.high_count} high · {scan.medium_count} medium · {scan.low_count} low
            </span>
            <span className="ml-auto text-[11px] text-slate-400">Last scanned: {fmt(scan.scanned_at)} · {scan.text_source}</span>
          </div>

          {scan.critical_count > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-2.5 text-sm text-rose-800 flex items-center gap-2" data-testid="cr-critical-banner">
              <AlertTriangle size={15} /> {scan.critical_count} critical clause(s) found — review before signing. Execs &amp; legal have been notified.
            </div>
          )}

          {/* Risk table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Level</th>
                  <th className="px-3 py-2">Clause Type</th>
                  <th className="px-3 py-2">Summary</th>
                  <th className="px-3 py-2">Why it's risky</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Pg</th>
                </tr>
              </thead>
              <tbody>
                {(scan.risks || []).length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No material risks identified. ✓</td></tr>
                )}
                {(scan.risks || []).map((r, i) => (
                  <tr key={i} className={`border-t border-slate-100 align-top ${ROW_TINT[r.risk_level] || ''}`} data-testid={`cr-risk-row-${i}`}>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${LEVEL_STYLE[r.risk_level] || LEVEL_STYLE.Low}`}>{r.risk_level}</span>
                    </td>
                    <td className="px-3 py-2.5 font-medium text-slate-700">{r.clause_type || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-700 max-w-[220px]">{r.clause_summary || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-500 max-w-[300px]">{r.risk_description || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-700">{r.recommended_action || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-400 text-xs">{r.page_reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* History */}
          {history.length > 1 && (
            <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="cr-history">
              <div className="text-[11px] font-display font-bold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1"><History size={12} /> Scan history</div>
              <div className="flex flex-wrap gap-2">
                {history.map((h) => (
                  <span key={h.id} className={`px-2 py-1 rounded text-[11px] border ${LEVEL_STYLE[h.overall_risk_level] || LEVEL_STYLE.Low}`}>
                    {fmt(h.scanned_at)} · {h.overall_risk_level} ({h.critical_count}C/{h.high_count}H)
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
